/**
 * THE BACKSTOP: ask the server whether this call is still a call.
 *
 * `call.*` events are the primary and by far the fastest way to learn that a
 * call ended, and nothing here changes that. But they are ALSO the only way,
 * and a `call.*` payload is explicitly designed with "no second chance to fetch
 * it" (apps/api/src/routes/calls.ts). One frame that never lands therefore
 * strands a user in a dead call until they give up and press Hang up — 68
 * minutes on 21 Aug, with a microphone publishing into an empty room for the
 * last 14 seconds of it and a control plane that had been dead for 16 minutes.
 *
 * The frame did not land because the socket was not there to land on. So this
 * asks the one question the socket cannot be trusted to answer, on the four
 * occasions when the answer might have changed without being heard:
 *
 *   • THE SOCKET CAME BACK. Whatever was published while it was gone is gone
 *     with it unless the gateway resumes the session, and it only resumes for
 *     `SESSION_RESUME_TTL_S` (5 minutes).
 *   • THE TAB CAME BACK. Focus and visibility, because a backgrounded tab is
 *     where every timer in the browser is throttled and every socket dies
 *     quietly.
 *   • THE SFU DROPPED US. The server evicts a stranded client by deleting the
 *     room; that is a reason to ask, never an answer in itself.
 *   • EVERY 30 SECONDS otherwise. One request. It is the difference between a
 *     dropped frame costing half a minute and costing the rest of the session.
 *
 * IT CANNOT END A LIVE CALL. There is exactly one way out of here, and it is
 * the server saying, in a 200, that the call's status is terminal. A failed
 * request, a 4xx, a 5xx, an offline tab, an SFU drop, an empty room and a
 * timeout all do NOTHING — the call is left exactly alone, which is the same
 * rule `reconcileWithSfu` follows on the server for the same reason.
 */
import { useEffect, useRef } from 'react';
import type { Call } from '@cyphex/types/calls';
import { getRealtimeClient } from '@/lib/realtime';
import { callsApi } from '../data/callsApi';
import { callHasFinished } from '../lib/callState';
import { onCallMediaDropped } from '../media/callMediaRegistry';

/** One request every half minute, and only while a call surface is mounted. */
const POLL_MS = 30_000;
/** Four triggers can fire at once. They collapse into one request. */
const DEBOUNCE_MS = 3_000;

/**
 * What the end screen says, per terminal status.
 *
 * Deliberately the same words `useCallLifecycle`'s `END_REASON` uses for the
 * matching event: whether the news arrived by socket or by fetch is an
 * implementation detail nobody should be able to read off the screen.
 */
const REASON: Readonly<Record<string, string>> = {
  ended: 'Call ended',
  missed: 'No answer',
  declined: 'Call declined',
  failed: 'Call failed',
  busy: 'Busy',
};

/**
 * WHAT TO SAY ABOUT A CALL THAT BECAME A MEETING, when the event never landed.
 *
 * `call.escalated` is the fast path and it carries the join code, which is the
 * only thing that can actually put this user into the meeting. Reconciliation
 * has no code — `GET /calls/:id` does not carry one and must not, since a call
 * is readable more widely than the meeting it turned into — so this can only
 * ever be an honest ending, never a navigation.
 *
 * It is still worth separating from "Call ended". `escalated_meeting_id` on the
 * call is a fact the server WILL give us, and telling somebody their call moved
 * to a meeting is the difference between looking for the meeting and assuming
 * the other person hung up.
 */
const endingFor = (call: Call): string => {
  /* A REAL ID, not merely "not null". The field is typed `MeetingId | null`, so
     TypeScript says this cannot be undefined — and a `!== null` test on a
     payload that simply OMITS the key answers "escalated" for every ordinary
     hangup in the product. Four tests in test/callEnd.test.mjs caught exactly
     that, because their fixtures are the shape a mock or an older API version
     sends rather than the shape the type promises. */
  const meetingId: unknown = call.escalatedMeetingId;
  if (typeof meetingId === 'string' && meetingId.length > 0) return 'Moved to a meeting';
  return REASON[call.status] ?? 'Call ended';
};

export type CallOutcome =
  | { readonly kind: 'ended'; readonly reason: string }
  /** Ended into ANOTHER call. Not an ending with a screen — see useCallLifecycle. */
  | { readonly kind: 'merged' };

export interface CallReconciliationInput {
  readonly callId: string;
  /** False once the call is already known to be over — stop asking. */
  readonly enabled: boolean;
  readonly onSettled: (outcome: CallOutcome) => void;
}

export function useCallReconciliation({
  callId,
  enabled,
  onSettled,
}: CallReconciliationInput): void {
  /* A ref, not a dep: the callback is re-created by its owner on every render
     and re-arming four listeners and a timer that often would be its own bug. */
  const settled = useRef(onSettled);
  settled.current = onSettled;

  useEffect(() => {
    if (callId === '' || !enabled) return undefined;

    let cancelled = false;
    let inFlight = false;
    let lastAskedAt = 0;
    /* A trigger that arrived too soon, waiting its turn. See `ask`. */
    let deferred: number | null = null;

    const ask = (): void => {
      /*
       * A DEBOUNCED TRIGGER IS DEFERRED, NEVER DROPPED.
       *
       * Four things can ask at once and they must collapse into one request —
       * but dropping the extras threw away the FASTEST of them. The mount ask
       * fires immediately and stamps `lastAskedAt`, so "the socket came back",
       * which lands within a second or two of a surface mounting on a
       * reconnect, was swallowed and the ending waited out the whole 30-second
       * poll. Now the first trigger inside the window arms one timer, every
       * other trigger sees it armed and returns, and exactly one request goes
       * out the moment the window closes — the same collapsing, without losing
       * the one trigger that was worth having.
       */
      if (cancelled || deferred !== null) return;
      const wait = inFlight ? DEBOUNCE_MS : DEBOUNCE_MS - (Date.now() - lastAskedAt);
      if (wait > 0) {
        deferred = window.setTimeout(() => {
          deferred = null;
          ask();
        }, wait);
        return;
      }
      lastAskedAt = Date.now();
      inFlight = true;
      void callsApi
        .detail(callId)
        .then((call) => {
          /*
           * ONLY A STATUS THIS CLIENT KNOWS TO BE TERMINAL ENDS ANYTHING.
           *
           * The rule was the inverse — anything NOT in a hardcoded live set was
           * treated as over, and `REASON[status] ?? 'Call ended'` supplied words
           * for it. Nothing triggers that today (the union and the live enum
           * agree exactly), which is what makes it a landmine rather than a
           * bug: `pstn_park_call` is already in-tree, and the first person to
           * add a non-terminal status without editing this file ends every such
           * call, and stops its microphone, within 30 seconds. Inference gets
           * the cautious default; the announcement path gets the other one.
           */
          if (cancelled || !callHasFinished(call.status)) return;
          /* A MERGE IS NOT AN ENDING WITH A SCREEN. The people on this call
             have been moved into the surviving one and are on their way there;
             `useCallLifecycle` explains at length why "Call ended" must not
             flash first. The status column cannot express that — it reads
             `ended` for both — so the REASON is what separates them, exactly as
             it does on the event. */
          settled.current(
            call.endReason === 'merged'
              ? { kind: 'merged' }
              : { kind: 'ended', reason: endingFor(call) },
          );
        })
        .catch(() => {
          /* THE ABSENCE OF AN ANSWER IS NOT AN ANSWER. Offline, a 502 through
             a restart, a 429, a socket dropped as the laptop woke — none of
             them is the server saying the call is over, and ending a live call
             on one would be a far worse bug than the one this fixes. */
        })
        .finally(() => {
          inFlight = false;
        });
    };

/* `window.setInterval`, not the bare global, so this file asks the same
       object it registers its listeners on. */
    const timer = window.setInterval(ask, POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') ask();
    };
    window.addEventListener('focus', ask);
    document.addEventListener('visibilitychange', onVisible);
    /* The socket coming back is the important one: everything published while
       it was away was published to nobody. */
    const offState = getRealtimeClient().onState((state) => {
      if (state === 'online') ask();
    });
    const offDropped = onCallMediaDropped((dropped) => {
      if (dropped === callId) ask();
    });

    /* And once on mount — a surface that comes up on a call that ended while
       the tab was closed must not wait 30 seconds to find out. */
    ask();

    return () => {
      cancelled = true;
      if (deferred !== null) window.clearTimeout(deferred);
      window.clearInterval(timer);
      window.removeEventListener('focus', ask);
      document.removeEventListener('visibilitychange', onVisible);
      offState();
      offDropped();
    };
  }, [callId, enabled]);
}
